import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type { IOrthOV1DiagnosticReportCustomControlData } from '../../../../../common/interfaces/ophthalmology/orthO/orthOV1DiagnosticReportCustomControlData';
import type { ValidationErrors } from '../../../../utils/validation';
import type { IDiagnosis } from '../../../interfaces/diagnosis';
import type { IEvaluation } from '../../evaluation/EvaluationRequestItem';
import type { IProcedureAddEdit } from '../../procedures/ProcedureItemAddEdit';
import type { ICustomControlProcedureDiagnosticReportAddEditProps } from '../interfaces/';
import { ConclusieDiagnoseOption } from '../../../../../common/enums/conclusieDiagnoseOption';
import { ProcessEntity } from '../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../common/enums/toastType';
import { translateKey } from '../../../../translate';
import { renameErrorKeys } from '../../../../utils/renameErrorKeys';
import { showServiceToast } from '../../../../utils/showToast';
import { SaveButton } from '../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer } from '../../../elements/common';
import { ProceedWithoutFiles } from '../../../elements/ProceedWithoutFiles';
import { ProcessContainer } from '../../episodes/common';
import { ReloadEpisode } from '../../episodes/reloadEpisode';
import { EvaluationRequestItem } from '../../evaluation/EvaluationRequestItem';
import { OrthCV1RequestAddEditForOrthO } from '../OrthC/v1/OrthCV1RequestAddEditForOrthO';
import { OrthOFolConvertToConsultButton } from '../OrthOFol/v1/OrthOFolConvertToConsultButton';
import { addFormAnalytics, addFormConversion } from '../utils/formAnalytics';
import { NewOrEditDiagnosticReportProcessContainer } from '../utils/v1/NewOrEditDiagnosticReportProcessContainer';
import { addMinusCylindrishValues, removeMinusCylindrishValues } from './OrthopticsV1ProcedureAddEdit';
import { OrthOV1DiagnosticReportAddEditCustomControlFields } from './OrthOV1DiagnosticReportAddEditCustomControlFields';

export interface IOrthopticsV1DiagnosticReportAddEditProps extends ICustomControlProcedureDiagnosticReportAddEditProps {
    isOrthOFol?: boolean;
}

export function OrthopticsV1DiagnosticReportAddEdit(props: IOrthopticsV1DiagnosticReportAddEditProps) {
    const formRef = React.useRef<HTMLFormElement>(null);
    const saveSendButtonRef = React.createRef<HTMLButtonElement>();
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);
    const [showEvaluation, setShowEvaluation] = useState(false);
    const [showProceedWithoutFiles, setShowProceedWithoutFiles] = useState<boolean>(false);
    const [hasConsultRequest, setHasConsultRequest] = useState(
        !props.isOrthOFol || props.procedure.customControlData.hasConsultRequestFields
    );

    const converToConsult = useCallback(() => {
        setHasConsultRequest(true);
    }, []);

    const buttons = useMemo(
        () => (
            <ButtonContainer
                alignRight={true}
                buttons={[
                    <CancelButton
                        cancel={props.onCancel}
                        entity={ProcessEntity.procedure}
                        isExisting={!!props.request.id}
                        revertChanges={props.revertProcedureChanges}
                        serviceCode={props.service.code}
                        serviceType={props.service.serviceType}
                    />,
                    <SaveButton
                        buttonDisabled={isSaveButtonDisabled}
                        save={async () => {
                            await onSave('draft');
                        }}
                    />,
                    <OrthOFolConvertToConsultButton
                        onConfirm={converToConsult}
                        procedure={props.procedure}
                    />,
                    <SaveSendButton
                        buttonDisabled={isSaveButtonDisabled}
                        saveSend={async () => {
                            await onSave('final');
                        }}
                        saveSendButtonRef={saveSendButtonRef}
                    />,
                ]}
            />
        ),
        [
            isSaveButtonDisabled,
            converToConsult,
            props.onCancel,
            props.procedure,
            props.request.id,
            props.service.code,
            props.service.serviceType,
            saveSendButtonRef,
        ]
    );

    useEffect(() => {
        addFormAnalytics(formRef.current, saveSendButtonRef.current);
    }, []);

    return (
        <>
            <NewOrEditDiagnosticReportProcessContainer
                currentDiagnosticReport={props.currentDiagnosticReport}
                dontShowPractitionerName={true}
                itemState={props.itemState}
                process={props.process}
                service={props.service}
                title={translateKey('diagnosticReport.findings')}
            >
                <OrthOV1DiagnosticReportAddEditCustomControlFields
                    customControlData={props.diagnosticReport.customControlData}
                    diagnosticReport={props.diagnosticReport}
                    errors={renameErrorKeys(props.errors, 'diagnosticReport.customControlData', 'customControlData')}
                    handleChange={props.handleChangeDiagnosticReport}
                    handleChangeCustomControl={async (change) => {
                        await props.handleChangeCustomControlDiagnosticReport(change);
                    }}
                    service={props.service}
                    validateOne={props.validateOneDiagnosticReport}
                    validateOneCustomControl={props.validateOneCustomControlDiagnosticReport}
                />
            </NewOrEditDiagnosticReportProcessContainer>

            {showProceedWithoutFiles && (
                <ProceedWithoutFiles
                    entity={ProcessEntity.request}
                    onNo={async () => {
                        setShowProceedWithoutFiles(false);
                        setIsSaveButtonDisabled(false);
                    }}
                    onYes={async () => {
                        setShowProceedWithoutFiles(false);
                        if (!hasConsultRequest) {
                            await saveProcedure('final');
                        } else {
                            setShowEvaluation(true);
                        }
                    }}
                    serviceType={props.service.serviceType}
                />
            )}

            {showEvaluation && (
                <EvaluationRequestItem
                    handleChange={async (name, value) => await handleEvaluationChange(name, value)}
                    onClose={async () => {
                        setShowEvaluation(false);
                        await saveProcedure('final');
                    }}
                />
            )}

            {hasConsultRequest && (
                <OrthCV1RequestAddEditForOrthO
                    consultData={props.procedure.customControlData.consult}
                    errors={props.errors}
                    handleChangeCustomControl={props.handleChangeCustomControl}
                    isFirstItem={props.isFirstItem}
                    itemState={props.itemState}
                    service={props.service}
                    validateOneCustomControl={props.validateOneCustomControl}
                />
            )}

            <ProcessContainer
                isActiveActivity={props.itemState?.initialIsOpen}
                noBorderTop
            >
                {buttons}
            </ProcessContainer>
        </>
    );

    async function onSave(saveType: 'draft' | 'final') {
        setIsSaveButtonDisabled(true);

        const updatedCylindrishValues = addMinusCylindrishValues(props.procedure.customControlData);
        await props.handleChangeCustomControl(updatedCylindrishValues);

        const validatedProcedure = await props.validateProcedure(saveType);
        if (!validatedProcedure) {
            const newCylindrishValues = removeMinusCylindrishValues(props.procedure.customControlData);
            await props.handleChangeCustomControl(newCylindrishValues);
            setIsSaveButtonDisabled(false);

            return;
        }

        if (
            saveType === 'final' &&
            props.procedure.assets.length === 0 &&
            (!props.procedure.customControlData.files || props.procedure.customControlData.files.length === 0)
        ) {
            return setShowProceedWithoutFiles(true);
        }

        if (saveType === 'final' && hasConsultRequest) {
            return setShowEvaluation(true);
        } else {
            saveProcedure(saveType);
        }
    }

    async function saveProcedure(saveType: 'draft' | 'final') {
        const requestId = await props.onSaveProcedure(saveType);
        if (!requestId) {
            setIsSaveButtonDisabled(false);

            return;
        }

        await props.onSave(requestId);

        if (saveType === 'draft') {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'procedure',
                translateKey('toast.draftDefaultMessage')
            );
        } else if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'procedure',
                translateKey('toast.submitDefaultMessage')
            );
            props.reloadProcess(ReloadEpisode.reloadAll);
            addFormConversion(formRef.current);
        }
    }

    async function handleEvaluationChange<K extends keyof IEvaluation>(name: K, value: IEvaluation[K]) {
        const consultData = props.procedure.customControlData.consult;
        consultData.evaluation = { ...consultData.evaluation, [name]: value };
        await props.handleChangeCustomControl({ consult: consultData });
    }
}

export function orthopticsV1ProcedureDiagnosticReportValidateOneCustomControlData(
    name: string,
    procedure: IProcedureAddEdit,
    errors: ValidationErrors
) {
    if (name === 'orthoptische_diagnose') {
        return validateOrthoptischeDiagnose(
            procedure.diagnosticReport?.customControlData,
            errors,
            procedure.diagnosticReport?.diagnosis
        );
    }

    return errors;
}

export function orthopticsV1ProcedureDiagnosticReportValidateCustomControlData(
    procedure: IProcedureAddEdit,
    errors: ValidationErrors
) {
    errors = validateOrthoptischeDiagnose(
        procedure.diagnosticReport?.customControlData,
        errors,
        procedure.diagnosticReport?.diagnosis
    );

    return errors;
}

function validateOrthoptischeDiagnose(
    customControlData: IOrthOV1DiagnosticReportCustomControlData,
    errors: ValidationErrors,
    diagnosis?: IDiagnosis
) {
    if (
        customControlData.heb_je_een_orthoptische_diagnose === ConclusieDiagnoseOption.nog_geen_diagnose ||
        customControlData.heb_je_een_orthoptische_diagnose === ConclusieDiagnoseOption.geen_afwijkingen
    ) {
        return errors.remove('customControlData.orthoptische_diagnose');
    }

    const orthoptische_diagnose = customControlData.orthoptische_diagnose;
    const optometrischeDiagnoseFilledIn =
        Object.keys(orthoptische_diagnose).some((item) => {
            return orthoptische_diagnose[item] !== false;
        }) ||
        diagnosis?.diagnosisConsiderationIcd10 ||
        diagnosis?.considerationThesaurusTermId;

    let validationErrors = errors;

    !optometrischeDiagnoseFilledIn
        ? (validationErrors = validationErrors.set('customControlData.orthoptische_diagnose', 'any_selected'))
        : (validationErrors = validationErrors.remove('customControlData.orthoptische_diagnose'));

    return validationErrors;
}
